import { create } from "zustand"

interface ReviewItemState {
	isEditing: boolean
	lastSavedValue: string
	showWarning: boolean
	isButtonDisabled: boolean
}

interface ReviewItemEditStore {
	currentEditId: string | null
	itemStates: Record<string, ReviewItemState>

	setCurrentEditId: (id: string | null) => void

	startEdit: (itemId: string, value: string) => void
	cancelEdit: (itemId: string) => void
	cancelAllEdits: () => void
	saveEdit: (itemId: string, value: string) => void
	setConflict: (itemId: string) => void
	resolveConflict: (itemId: string) => void
	updateLastSavedValue: (itemId: string, value: string) => void
}

const clearWarnings = (itemStates: Record<string, ReviewItemState>) => {
	return Object.entries(itemStates).reduce<Record<string, ReviewItemState>>(
		(accumulator, [itemId, state]) => {
			accumulator[itemId] = {
				...state,
				showWarning: false,
				isButtonDisabled: false
			}
			return accumulator
		},
		{}
	)
}

export const useReviewItemEditStore = create<ReviewItemEditStore>((set) => ({
	currentEditId: null,
	itemStates: {},

	setCurrentEditId: (id) => set({ currentEditId: id }),

	startEdit: (itemId, value) => {
		set((state) => {
			const isAnotherFieldEditing =
				state.currentEditId &&
				state.currentEditId !== itemId &&
				state.itemStates[state.currentEditId]?.isEditing

			if (isAnotherFieldEditing) {
				return {
					itemStates: {
						...state.itemStates,
						[itemId]: {
							isEditing: false,
							lastSavedValue: state.itemStates[itemId]?.lastSavedValue ?? value,
							showWarning: true,
							isButtonDisabled: false
						}
					}
				}
			}

			return {
				currentEditId: itemId,
				itemStates: {
					...clearWarnings(state.itemStates),
					[itemId]: {
						isEditing: true,
						lastSavedValue: value,
						showWarning: false,
						isButtonDisabled: false
					}
				}
			}
		})
	},

	cancelEdit: (itemId) => {
		set((state) => ({
			currentEditId: null,
			itemStates: {
				...clearWarnings(state.itemStates),
				[itemId]: {
					...state.itemStates[itemId],
					isEditing: false,
					showWarning: false,
					isButtonDisabled: false
				}
			}
		}))
	},

	cancelAllEdits: () => {
		set((state) => {
			const newItemStates = { ...state.itemStates }
			for (const item in newItemStates) {
				newItemStates[item].isEditing = false
				newItemStates[item].showWarning = false
				newItemStates[item].isButtonDisabled = false
			}
			return {
				itemStates: newItemStates,
				currentEditId: null
			}
		})
	},

	saveEdit: (itemId, value) => {
		set((state) => ({
			currentEditId: null,
			itemStates: {
				...clearWarnings(state.itemStates),
				[itemId]: {
					...state.itemStates[itemId],
					isEditing: false,
					lastSavedValue: value,
					showWarning: false,
					isButtonDisabled: false
				}
			}
		}))
	},

	setConflict: (itemId) => {
		set((state) => ({
			itemStates: {
				...state.itemStates,
				[itemId]: {
					...state.itemStates[itemId],
					showWarning: true,
					isButtonDisabled: true
				}
			}
		}))
	},

	resolveConflict: (itemId) => {
		set((state) => ({
			itemStates: {
				...state.itemStates,
				[itemId]: {
					...state.itemStates[itemId],
					showWarning: false,
					isButtonDisabled: false
				}
			}
		}))
	},

	updateLastSavedValue: (itemId, value) => {
		set((state) => ({
			itemStates: {
				...state.itemStates,
				[itemId]: {
					...state.itemStates[itemId],
					lastSavedValue: value
				}
			}
		}))
	}
}))
